/**
 * @file cc-credit-cart.js
 * Credit cart feature module for the Credit Calculator Wizards pages.
 *
 * Responsibilities:
 *  - `creditCartCounterSync` — shared helper (window-exposed) that keeps the
 *    `.credit-cart-counter` badge in sync with the server-side cart count.
 *  - Delegated `.credit-cart` click handler — adds or removes individual
 *    precedent items, updates the UI in place, and fires document-level events
 *    (`creditCart:itemAdded`, `creditCart:itemRemoved`) for other modules to observe.
 *  - `#clear-credit-cart` click handler — bulk-removes all cart items.
 *  - `creditCartToast()` — brief success notification utility.
 *  - `emptyCartMessage()` — replaces the results area when the cart is empty.
 */


/**
 * Shared credit cart counter sync helper.
 * Re-uses any existing instance already attached to `window` (e.g. set by another page),
 * otherwise creates a new one. Exposed on `window` so non-module scripts can reach it.
 *
 * @type {{ applyCount: function(number): void, refresh: function(): jqXHR }}
 */
var creditCartCounterSync =
    window.creditCartCounterSync ||
    (function () {
        /**
         * Updates every `.credit-cart-counter` element with the given count.
         *
         * @param {number} count The number of items currently in the credit cart.
         */
        function applyCount(count) {
            $(".credit-cart-counter").text(count);
        }

        /**
         * Fetches the current cart item count from the server and applies it via {@link applyCount}.
         *
         * @returns {jqXHR} The jQuery AJAX request object.
         */
        function refresh() {
            return $.get("/credit-calculator-wizards/getCreditCartCount").always(
                function (count) {
                    applyCount(count);
                },
            );
        }

        return {
            applyCount: applyCount,
            refresh: refresh,
        };
    })();

window.creditCartCounterSync = creditCartCounterSync;

export { creditCartCounterSync };

/**
 * Delegated click handler for all `.credit-cart` buttons.
 * Reads the `action` attribute of the clicked button (`"add"` or `"delete"`) and
 * POSTs the corresponding server request.
 *
 * - **add**: POSTs to `addCreditCartItem`, replaces the button with an "Added / Remove" widget,
 *   refreshes the cart counter, and shows a toast. Fires the `creditCart:itemAdded` document event.
 * - **delete**: POSTs to `deleteCreditCartItem`, restores the plain "Add" button,
 *   refreshes the cart counter, and shows a toast. Fires the `creditCart:itemRemoved` document event.
 *   If the button also has the `pop` class the row (or its parent result block) is removed from the DOM;
 *   if that empties the results list, {@link emptyCartMessage} is called.
 */
$(document).on("click", ".credit-cart", function () {
    var $button = $(this);
    var precedentId = $button.attr("precedentID");
    var $container = $button.closest("td");

    if ($button.attr("action") === "add") {
        var addedHtml = `
            <div class="d-flex flex-column align-items-center text-center">
                <span class="d-inline-flex align-items-center mb-2" style="color: #7C9627; font-weight: 700;">
                    <span class="ms-1">Added</span>
                </span>
                <button type="button" class="btn btn-action btn-sm credit-cart"
                    action="delete" precedentID="${precedentId}" aria-label="Remove from cart">
                    Remove
                </button>
            </div>`;

        $.post(
            `/credit-calculator-wizards/addCreditCartItem?id=${precedentId}`,
            { _csrfToken: $('[name="_csrfToken"]').val() },
        ).always(function (data) {
            if (data.status === 200) {
                $container.html(addedHtml);
                creditCartCounterSync.refresh();
                $(document).trigger("creditCart:itemAdded", [precedentId]);
            } else {
                alert("Failed to add to Credit Cart.");
            }
        });

        creditCartToast("Added to Credit Cart.");
    } else if ($button.attr("action") === "delete") {
        var addHtml = `<button type="button" class="btn btn-action btn-sm credit-cart"
            action="add" precedentID="${precedentId}" aria-label="Add to cart">Add</button>`;

        $.post(
            `/credit-calculator-wizards/deleteCreditCartItem?id=${precedentId}`,
            { _csrfToken: $('[name="_csrfToken"]').val() },
        ).always(function (data) {
            if (data.status === 200) {
                $container.html(addHtml);
                creditCartCounterSync.refresh();
                $(document).trigger("creditCart:itemRemoved", [precedentId]);
                creditCartToast("Removed from Credit Cart.");
            } else {
                alert("Failed to delete from Credit Cart.");
            }
        });

        if ($button.hasClass("pop")) {
            if ($button.closest(".result").find("tr").length === 2) {
                $button.closest(".result").remove();
            } else if ($button.closest("tbody").find("tr").length === 1) {
                $button.closest("table").remove();
            } else {
                $button.closest("tr").remove();
            }

            if ($("#search-results").find(".result").length === 0) {
                emptyCartMessage();
            }
        }
    }
});

/**
 * Displays a brief success toast notification at the bottom of the page.
 * The toast fades in immediately and is automatically removed after 3 seconds.
 *
 * @param {string} message The message text to display inside the toast.
 */
function creditCartToast(message) {
    $("#toaster").append(
        `<div class="col-md-2 alert alert-success toast" style="display: none; z-index: 10">
            <strong>${message}</strong>
        </div>`,
    );

    $("#toaster").find(".toast").fadeIn();
    window.setTimeout(function () {
        $("#toaster")
            .find(".toast")
            .fadeOut(function () {
                $("#toaster").find(".toast").remove();
            });
    }, 3000);
}

/**
 * Handles clicks on the "Clear credit cart" button.
 * POSTs a wildcard delete to remove all items from the server-side cart,
 * refreshes the counter on success, then immediately clears the results display.
 */
$("#clear-credit-cart").click(function () {
    $.post("/credit-calculator-wizards/deleteCreditCartItem?id=*", {
        _csrfToken: $('[name="_csrfToken"]').val(),
    }).always(function (data) {
        if (data.status === 200) {
            creditCartCounterSync.refresh();
        } else {
            alert("Failed to clear to Credit Cart.");
        }
    });

    emptyCartMessage();
});

/**
 * Replaces the contents of `#search-results` with an "empty cart" message.
 * Called when all items are removed from the cart view.
 */
function emptyCartMessage() {
    $("#search-results")
        .empty()
        .append(
            '<p style="text-align: center; font-weight: bold">Your credit cart is empty.</p>',
        );
}
